import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Grid,
    Paper,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material';
import {
    ArrowForward as ArrowForwardIcon,
    Business as BusinessIcon,
    LocationOn as LocationOnIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { initializeDB } from '../../../config/db';
import { CSEP_STORE, JOB_STORE } from '../../../config/constants';
import { createCsepSkeleton, ensureCsepStructure } from '../utils/csepRecordFactory';
import { PRE_ENTRY_QUESTIONS, MODE_JOB, MODE_LOCATION } from '../utils/constants';
import { getFullNameEmployeeBy } from '../../../services/employeeService';
import CsepStepHeader from '../CsepStepHeader';
import SelectEmployeesAndTempsModal from '../../jha/SelectEmployeesAndTempsModal';
import { localDateTimeString } from '../../../utils/localDate';

const NOW_DATETIME = () => localDateTimeString(); // local datetime (not UTC)

function buildLocationOptions(jobs) {
    const seen = new Map();
    jobs.forEach(job => {
        const rawLocation = job.location || job.Location || '';
        if (!rawLocation) return;
        const key = rawLocation.trim().toLowerCase();
        if (!seen.has(key)) {
            seen.set(key, { label: rawLocation, jobCodes: [job.Job || job.jobNumber] });
        } else {
            const entry = seen.get(key);
            const code = job.Job || job.jobNumber;
            if (code && !entry.jobCodes.includes(code)) entry.jobCodes.push(code);
        }
    });
    return Array.from(seen.values());
}

export default function CsepStepOne() {
    const navigate = useNavigate();
    const { state } = useLocation() || {};
    const { csep = null, isNew = false, viewMode = false, fromJha = false, jhaId = null } = state || {};

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [csepData, setCsepData] = useState(null);
    const [jobs, setJobs] = useState([]);
    const [supervisorModalOpen, setSupervisorModalOpen] = useState(false);

    const saveTimeoutRef = useRef(null);
    const pendingRecordRef = useRef(null);
    const createdRecordIdRef = useRef(csep?.uuid || null);

    const isSubmitted = csepData?.status === 'submitted' || csepData?.status === 'completed';
    const isReadOnly = isSubmitted || viewMode;

    const locationOptions = useMemo(() => buildLocationOptions(jobs), [jobs]);

    const sortedJobs = useMemo(() => {
        return [...jobs].sort((a, b) => {
            const jobA = parseInt(a.Job || a.jobNumber) || 0;
            const jobB = parseInt(b.Job || b.jobNumber) || 0;
            return jobB - jobA;
        });
    }, [jobs]);

    useEffect(() => {
        loadData();
        return () => { if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current); };
    }, []);

    const loadData = useCallback(async () => {
        setLoading(true);
        const db = await initializeDB();

        const allJobs = await db.getAll(JOB_STORE);
        setJobs(allJobs || []);

        // Get logged-in user as default supervisor
        const employeeId = localStorage.getItem('employeeId');
        const currentEmployee = employeeId ? await getFullNameEmployeeBy(employeeId) : null;

        if (!db.objectStoreNames.contains(CSEP_STORE)) {
            console.error('CSEP store not found');
            setLoading(false);
            return;
        }

        const tx = db.transaction(CSEP_STORE, 'readwrite');
        const store = tx.objectStore(CSEP_STORE);
        let currentRecord = null;

        if (createdRecordIdRef.current) currentRecord = await store.get(createdRecordIdRef.current);
        if (!currentRecord && csep?.uuid) currentRecord = await store.get(csep.uuid);
        if (!currentRecord && isNew) {
            // Create in memory only - don't save until user makes changes
            currentRecord = createCsepSkeleton({
                stepOne: {
                    dateTime: NOW_DATETIME(),
                    entrySupervisorId: currentEmployee?.EE || currentEmployee?.employeeId || null,
                    entrySupervisorName: currentEmployee?.FullName || '',
                    entrySupervisorPhone: currentEmployee?.CellPhone || currentEmployee?.Phone || '',
                    entrySupervisorType: 'employee'
                }
            });
        }
        if (!currentRecord && csep) {
            currentRecord = ensureCsepStructure(csep);
            await store.put(currentRecord);
            createdRecordIdRef.current = currentRecord.uuid;
        }
        if (!currentRecord) { setLoading(false); navigate('/csep'); return; }

        setCsepData(ensureCsepStructure(currentRecord));
        setLoading(false);
    }, [isNew, csep, navigate]);

    const flushSave = useCallback(async () => {
        if (!pendingRecordRef.current) { setSaving(false); return; }
        const recordToSave = pendingRecordRef.current;
        pendingRecordRef.current = null;
        const db = await initializeDB();
        const tx = db.transaction(CSEP_STORE, 'readwrite');
        await tx.objectStore(CSEP_STORE).put(recordToSave);
        await tx.done;
        createdRecordIdRef.current = recordToSave.uuid; // Track saved record
        setSaving(false);
    }, []);

    const scheduleSave = useCallback((record, options = {}) => {
        if (isReadOnly) { setCsepData(record); return; }
        const recordWithTimestamp = { ...record, updatedAt: new Date().toISOString() };
        setCsepData(recordWithTimestamp);
        pendingRecordRef.current = recordWithTimestamp;
        setSaving(true);
        if (options.immediate) { flushSave(); return; }
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = setTimeout(() => flushSave(), 400);
    }, [flushSave, isReadOnly]);

    const updateStepOne = (field, value) => {
        if (!csepData || isReadOnly) return;
        scheduleSave({ ...csepData, stepOne: { ...csepData.stepOne, [field]: value } });
    };

    const handleModeChange = (_, newMode) => {
        if (!newMode || isReadOnly) return;
        scheduleSave({
            ...csepData,
            stepOne: { ...csepData.stepOne, mode: newMode, jobId: null, jobCode: '', jobName: '', location: '' }
        });
    };

    const handleJobSelect = (_, job) => {
        if (isReadOnly) return;
        if (job) {
            scheduleSave({
                ...csepData,
                stepOne: {
                    ...csepData.stepOne,
                    jobId: job.id || job.ID || null,
                    jobCode: job.Job || job.jobNumber || '',
                    jobName: job.JobName || job.jobName || '',
                    location: job.location || job.Location || job.JobName || job.jobName || ''
                },
                stepTwo: {
                    ...csepData.stepTwo,
                    project: job.JobName || job.jobName || '',
                    supervisor: job.Manager || '',
                    confinedSpaceLocation: job.location || job.Location || ''
                }
            });
        } else {
            scheduleSave({
                ...csepData,
                stepOne: { ...csepData.stepOne, jobId: null, jobCode: '', jobName: '', location: '' }
            });
        }
    };

    const handleLocationSelect = (_, loc) => {
        if (isReadOnly) return;
        if (loc) {
            scheduleSave({
                ...csepData,
                stepOne: { ...csepData.stepOne, location: loc.label },
                stepTwo: { ...csepData.stepTwo, confinedSpaceLocation: loc.label }
            });
        } else {
            scheduleSave({ ...csepData, stepOne: { ...csepData.stepOne, location: '' } });
        }
    };

    const handleSupervisorSelect = (selected) => {
        if (isReadOnly || !selected || selected.length === 0) return;
        const person = selected[0];
        scheduleSave({
            ...csepData,
            stepOne: {
                ...csepData.stepOne,
                entrySupervisorId: person.EE || person.ee || person.id || null,
                entrySupervisorName: person.FullName || '',
                entrySupervisorPhone: person.CellPhone || person.Phone || person.phone || '',
                entrySupervisorType: person._type || 'employee'
            }
        });
        setSupervisorModalOpen(false);
    };

    const handleAnswerChange = (questionId, answer) => {
        if (isReadOnly) return;
        scheduleSave({
            ...csepData,
            stepOne: { ...csepData.stepOne, preEntryAnswers: { ...csepData.stepOne.preEntryAnswers, [questionId]: answer } }
        });
    };

    const handleNext = async () => {
        const updated = { ...csepData, meta: { ...csepData.meta, lastStep: Math.max(csepData.meta.lastStep, 2) } };
        pendingRecordRef.current = updated;
        await flushSave();
        navigate('/csep/step-2', { state: { csep: updated, isNew: false, viewMode, fromJha, jhaId } });
    };

    const answeredCount = useMemo(() => {
        if (!csepData?.stepOne?.preEntryAnswers) return 0;
        return Object.values(csepData.stepOne.preEntryAnswers).filter(v => v !== null).length;
    }, [csepData?.stepOne?.preEntryAnswers]);

    const allAnswered = answeredCount === PRE_ENTRY_QUESTIONS.length;

    if (loading || !csepData) {
        return <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}><CircularProgress /></Box>;
    }

    const stepOne = csepData.stepOne;
    const selectedJob = sortedJobs.find(j => (j.Job || j.jobNumber) === stepOne.jobCode) || null;
    const selectedLocation = stepOne.location ? locationOptions.find(opt => opt.label === stepOne.location) || null : null;

    const alreadySelectedSupervisor = stepOne.entrySupervisorName ? [{
        FullName: stepOne.entrySupervisorName,
        _type: stepOne.entrySupervisorType || 'employee',
        EE: stepOne.entrySupervisorId
    }] : [];

    return (
        <Box sx={{ height: 'calc(100vh - 64px)', backgroundColor: '#f5f5f5', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <CsepStepHeader currentStep={1} csepData={csepData} />

            <Box sx={{ flex: 1, overflow: 'auto', px: 1, py: 0.5 }}>
                {/* Header Row */}
                <Paper sx={{ p: 1, mb: 0.5 }}>
                    <Grid container spacing={1} alignItems="center">
                        <Grid item xs={12} sm={4}>
                            <ToggleButtonGroup value={stepOne.mode} exclusive onChange={handleModeChange} size="small" disabled={isReadOnly}>
                                <ToggleButton value={MODE_JOB}><BusinessIcon sx={{ fontSize: 16, mr: 0.5 }} />Job</ToggleButton>
                                <ToggleButton value={MODE_LOCATION}><LocationOnIcon sx={{ fontSize: 16, mr: 0.5 }} />Location</ToggleButton>
                            </ToggleButtonGroup>
                        </Grid>
                        <Grid item xs={12} sm={8}>
                            {stepOne.mode === MODE_JOB ? (
                                <Autocomplete
                                    options={sortedJobs}
                                    value={selectedJob}
                                    onChange={handleJobSelect}
                                    getOptionLabel={(o) => {
                                        const code = o.Job || o.jobNumber || '';
                                        const name = o.JobName || o.jobName || '';
                                        return code && name ? `${code} • ${name}` : (code || name || 'Unnamed job');
                                    }}
                                    isOptionEqualToValue={(option, value) => (option.Job || option.jobNumber) === (value.Job || value.jobNumber)}
                                    renderInput={(params) => <TextField {...params} size="small" placeholder="Select job" />}
                                    disabled={isReadOnly}
                                    size="small"
                                />
                            ) : (
                                <Autocomplete
                                    options={locationOptions}
                                    value={selectedLocation}
                                    onChange={handleLocationSelect}
                                    getOptionLabel={(o) => o.label || ''}
                                    isOptionEqualToValue={(option, value) => option.label === value.label}
                                    renderInput={(params) => <TextField {...params} size="small" placeholder="Search location" />}
                                    disabled={isReadOnly}
                                    size="small"
                                />
                            )}
                        </Grid>
                    </Grid>
                    <Grid container spacing={1} sx={{ mt: 0.5 }} alignItems="center">
                        <Grid item xs={6} sm={4}>
                            <TextField
                                label="Date & Time"
                                type="datetime-local"
                                value={stepOne.dateTime || ''}
                                onChange={(e) => updateStepOne('dateTime', e.target.value)}
                                size="small"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                disabled={isReadOnly}
                            />
                        </Grid>
                        <Grid item xs={6} sm={8}>
                            <TextField
                                label="Entry Supervisor"
                                value={stepOne.entrySupervisorName || ''}
                                size="small"
                                fullWidth
                                InputProps={{ readOnly: true }}
                                onClick={() => !isReadOnly && setSupervisorModalOpen(true)}
                                sx={{ cursor: isReadOnly ? 'default' : 'pointer' }}
                            />
                        </Grid>
                    </Grid>
                </Paper>

                {/* Questions Grid */}
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                    <Grid container spacing={0.5}>
                        {PRE_ENTRY_QUESTIONS.map((q) => {
                            const answer = stepOne.preEntryAnswers[q.id];
                            return (
                                <Grid item xs={12} sm={6} key={q.id}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 0.65,
                                            border: '1px solid #e0e0e0',
                                            backgroundColor: answer === 'no' ? '#ffebee' : '#fafafa',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5
                                        }}
                                    >
                                        <Typography variant="body2" sx={{ fontSize: '0.725rem', lineHeight: 1.3, flex: 1 }}>
                                            <strong>{q.id}.</strong> {q.text}
                                        </Typography>
                                        <ToggleButtonGroup
                                            value={answer}
                                            exclusive
                                            onChange={(_, v) => v && handleAnswerChange(q.id, v)}
                                            size="small"
                                            disabled={isReadOnly}
                                            sx={{
                                                flexShrink: 0,
                                                '& .MuiToggleButton-root': {
                                                    px: 1,
                                                    py: 0.25,
                                                    fontSize: '0.7rem',
                                                    minHeight: 28,
                                                    minWidth: 32,
                                                    '&.Mui-selected': { fontWeight: 600 }
                                                }
                                            }}
                                        >
                                            <ToggleButton value="yes" sx={{ '&.Mui-selected': { backgroundColor: '#4caf50', color: '#fff', '&:hover': { backgroundColor: '#45a049' } } }}>YES</ToggleButton>
                                            <ToggleButton value="no" sx={{ '&.Mui-selected': { backgroundColor: '#f44336', color: '#fff', '&:hover': { backgroundColor: '#d32f2f' } } }}>NO</ToggleButton>
                                            <ToggleButton value="na" sx={{ '&.Mui-selected': { backgroundColor: '#2196f3', color: '#fff', '&:hover': { backgroundColor: '#1e88e5' } } }}>N/A</ToggleButton>
                                        </ToggleButtonGroup>
                                    </Paper>
                                </Grid>
                            );
                        })}
                    </Grid>
                </Box>
            </Box>

            {/* Bottom Navigation */}
            <Box sx={{
                backgroundColor: allAnswered ? '#e8f5e9' : '#fff3e0',
                borderTop: allAnswered ? '1px solid #a5d6a7' : '1px solid #ffcc80',
                px: 1,
                py: 0.5
            }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" sx={{ fontWeight: 500, color: allAnswered ? '#2e7d32' : '#e65100' }}>
                        {allAnswered
                            ? '✓ All questions answered'
                            : `Do NOT enter until all answered YES or N/A. Progress: ${answeredCount}/${PRE_ENTRY_QUESTIONS.length}`
                        }
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        {saving && <Typography variant="caption" color="text.secondary">Saving...</Typography>}
                        <Button
                            variant="contained"
                            size="small"
                            endIcon={<ArrowForwardIcon />}
                            onClick={handleNext}
                            disabled={!allAnswered}
                        >
                            Continue
                        </Button>
                    </Stack>
                </Stack>
            </Box>

            <SelectEmployeesAndTempsModal
                open={supervisorModalOpen}
                onClose={() => setSupervisorModalOpen(false)}
                onSelect={handleSupervisorSelect}
                alreadySelected={alreadySelectedSupervisor}
                singleSelect={true}
            />
        </Box>
    );
}
